import React, { useState } from 'react';
import { apiFetch } from '../lib/api';
import ModaleCercaRecord from './ModaleCercaRecord';
import ModaleCercaSportello, { Sportello } from './ModaleCercaSportello';

// ─────────────────────────────────────────────────────────────────────────────
// BancaFormFields — campi dati bancari riutilizzabili (cliente e fornitore).
//
// Gestisce IBAN, ABI, CAB, Banca, Filiale con:
//  - validazione IBAN offline (checksum) + estrazione ABI/CAB dal backend
//  - auto-compilazione nome banca dal codice ABI (anagrafica nazionale locale)
//  - generazione IBAN da ABI + CAB + numero conto (calcolo offline)
//
// Nessuna API esterna: tutto via /api/anagrafica-banche.
// ─────────────────────────────────────────────────────────────────────────────

export interface BancaValue {
  iban: string;
  abi: string;
  cab: string;
  banca: string;
  filialeBanca: string;
}

interface IbanInfo {
  valid: boolean;
  iban: string | null;
  abi: string | null;
  cab: string | null;
  cin: string | null;
  conto: string | null;
  denominazione: string | null;
  filiale: string | null;
  errore: string | null;
}

interface BancaLite { abi: string; denominazione: string; }

interface Props {
  value: BancaValue;
  onChange: (patch: Partial<BancaValue>) => void;
  disabled?: boolean;
}

type Stato = 'idle' | 'checking' | 'valid' | 'invalid';

const soloCifre = (s: string, max: number) => s.replace(/\D/g, '').slice(0, max);

export default function BancaFormFields({ value, onChange, disabled }: Props) {
  const [stato, setStato] = useState<Stato>('idle');
  const [msg, setMsg] = useState<string>('');
  const [abiMsg, setAbiMsg] = useState<string>('');
  const [conto, setConto] = useState<string>('');
  const [composing, setComposing] = useState<boolean>(false);

  // Picker banca / sportello
  const [showBancaModal, setShowBancaModal] = useState(false);
  const [showSportelloModal, setShowSportelloModal] = useState(false);
  const [bancheList, setBancheList] = useState<BancaLite[]>([]);

  // Apre il picker banca caricando l'elenco completo (registro ~812 banche)
  const openBancaPicker = async () => {
    if (bancheList.length === 0) {
      try {
        // @api GET /api/anagrafica-banche -> {"success":"boolean","data":"BancaLite[]"}
        const res = await apiFetch<{ success: boolean; data: BancaLite[] }>('/api/anagrafica-banche');
        setBancheList(res.data || []);
      } catch {
        setBancheList([]);
      }
    }
    setShowBancaModal(true);
  };

  // Selezione banca dal picker → compila ABI + Banca
  const onSelectBanca = (record: any) => {
    onChange({ abi: record.abi, banca: record.denominazione });
    setAbiMsg('');
    setShowBancaModal(false);
  };

  // Selezione sportello dal picker → compila CAB + Filiale (+ ABI/Banca se mancanti)
  const onSelectSportello = async (s: Sportello) => {
    const patch: Partial<BancaValue> = { abi: s.abi, cab: s.cab, filialeBanca: s.sportello };
    if (!value.banca) {
      try {
        const res = await apiFetch<{ success: boolean; data: { denominazione: string } | null }>(`/api/anagrafica-banche/abi/${s.abi}`);
        if (res.data) patch.banca = res.data.denominazione;
      } catch { /* nome banca resta modificabile a mano */ }
    }
    onChange(patch);
    setShowSportelloModal(false);
  };

  // CAB → Filiale: al blur del CAB, se ABI+CAB validi, ricava la filiale
  const lookupFiliale = async () => {
    const a = (value.abi || '').trim();
    const c = (value.cab || '').trim();
    if (!/^\d{5}$/.test(a) || !/^\d{5}$/.test(c)) return;
    try {
      // @api GET /api/anagrafica-banche/sportello/:abi/:cab -> {"success":"boolean","data":{"sportello":"string","citta":"string"}}
      const res = await apiFetch<{ success: boolean; data: { sportello: string } | null }>(`/api/anagrafica-banche/sportello/${a}/${c}`);
      if (res.data) onChange({ filialeBanca: res.data.sportello });
    } catch { /* silenzioso */ }
  };

  // Verifica IBAN: valida + estrae ABI/CAB + ricava nome banca
  const verificaIban = async () => {
    const v = (value.iban || '').trim();
    if (!v) { setStato('idle'); setMsg(''); return; }
    setStato('checking'); setMsg('Verifica in corso…');
    try {
      // @api POST /api/anagrafica-banche/iban-info {"iban":"string"} -> {"success":"boolean","data":"IbanInfo"}
      const res = await apiFetch<{ success: boolean; data: IbanInfo }>('/api/anagrafica-banche/iban-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ iban: v }),
      });
      const d = res.data;
      if (d.valid) {
        onChange({
          iban: d.iban || v,
          abi: d.abi || value.abi,
          cab: d.cab || value.cab,
          banca: d.denominazione || value.banca,
          filialeBanca: d.filiale || value.filialeBanca,
        });
        setStato('valid');
        setMsg(d.denominazione ? `IBAN valido — ${d.denominazione}` : 'IBAN valido (banca non presente in anagrafica)');
      } else {
        setStato('invalid');
        setMsg(d.errore || 'IBAN non valido');
      }
    } catch (err: any) {
      setStato('invalid');
      setMsg(err?.message || 'Errore nella verifica IBAN');
    }
  };

  // Ricava il nome banca dal codice ABI
  const lookupAbi = async () => {
    const a = (value.abi || '').trim();
    setAbiMsg('');
    if (!/^\d{5}$/.test(a)) return;
    try {
      // @api GET /api/anagrafica-banche/abi/:abi -> {"success":"boolean","data":{"abi":"string","denominazione":"string"}}
      const res = await apiFetch<{ success: boolean; data: { abi: string; denominazione: string } | null }>(`/api/anagrafica-banche/abi/${a}`);
      if (res.data) {
        onChange({ banca: res.data.denominazione });
      } else {
        setAbiMsg('ABI non presente in anagrafica');
      }
    } catch {
      /* silenzioso: il nome banca resta modificabile a mano */
    }
  };

  // Genera IBAN da ABI + CAB + numero conto (calcolo offline)
  const generaIban = async () => {
    const a = (value.abi || '').trim();
    const c = (value.cab || '').trim();
    const cc = conto.trim();
    if (!/^\d{5}$/.test(a) || !/^\d{5}$/.test(c) || !cc) {
      setMsg('Servono ABI (5 cifre), CAB (5 cifre) e numero conto');
      setStato('invalid');
      return;
    }
    setComposing(true);
    try {
      // @api POST /api/anagrafica-banche/iban-info {"abi":"string","cab":"string","conto":"string"} -> {"success":"boolean","data":"IbanInfo"}
      const res = await apiFetch<{ success: boolean; data: IbanInfo }>('/api/anagrafica-banche/iban-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ abi: a, cab: c, conto: cc }),
      });
      const d = res.data;
      if (d.valid && d.iban) {
        onChange({ iban: d.iban, banca: d.denominazione || value.banca, filialeBanca: d.filiale || value.filialeBanca });
        setStato('valid');
        setMsg(`IBAN generato${d.denominazione ? ` — ${d.denominazione}` : ''}`);
        setConto('');
      } else {
        setStato('invalid');
        setMsg(d.errore || 'Impossibile generare IBAN');
      }
    } catch (err: any) {
      setStato('invalid');
      setMsg(err?.message || 'Errore nella generazione IBAN');
    } finally {
      setComposing(false);
    }
  };

  return (
    <div className="bop-banca-fields">
      {/* IBAN + verifica */}
      <div className="bop-form-group">
        <label className="bop-label">IBAN</label>
        <div className="bop-banca-fields__iban-row">
          <input
            className="bop-input"
            type="text"
            value={value.iban}
            onChange={(e) => { onChange({ iban: e.target.value.toUpperCase() }); setStato('idle'); setMsg(''); }}
            onBlur={verificaIban}
            placeholder="IT00A0000000000000000000000"
            disabled={disabled}
          />
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={verificaIban}
            disabled={disabled || stato === 'checking' || !value.iban}
          >
            Verifica
          </button>
        </div>
        {msg && (
          <div className={`bop-banca-fields__msg ${stato === 'invalid' ? 'bop-text-danger' : 'bop-text-muted'}`}>
            {stato === 'valid' ? '✓ ' : stato === 'invalid' ? '✕ ' : ''}{msg}
          </div>
        )}
      </div>

      {/* ABI + CAB */}
      <div className="bop-anagrafica__form-grid-2">
        <div className="bop-form-group">
          <label className="bop-label">ABI</label>
          <input
            className="bop-input"
            type="text"
            inputMode="numeric"
            value={value.abi}
            onChange={(e) => onChange({ abi: soloCifre(e.target.value, 5) })}
            onBlur={lookupAbi}
            placeholder="5 cifre"
            disabled={disabled}
          />
          {abiMsg && <div className="bop-banca-fields__msg bop-text-muted">{abiMsg}</div>}
        </div>
        <div className="bop-form-group">
          <label className="bop-label">CAB</label>
          <input
            className="bop-input"
            type="text"
            inputMode="numeric"
            value={value.cab}
            onChange={(e) => onChange({ cab: soloCifre(e.target.value, 5) })}
            onBlur={lookupFiliale}
            placeholder="5 cifre"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Genera IBAN da ABI + CAB + conto */}
      <div className="bop-form-group">
        <label className="bop-label">Genera IBAN da numero conto</label>
        <div className="bop-banca-fields__iban-row">
          <input
            className="bop-input"
            type="text"
            value={conto}
            onChange={(e) => setConto(e.target.value.toUpperCase().replace(/\s+/g, ''))}
            placeholder="Numero conto (max 12)"
            disabled={disabled}
          />
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={generaIban}
            disabled={disabled || composing || !value.abi || !value.cab || !conto}
          >
            {composing ? 'Genero…' : 'Genera IBAN'}
          </button>
        </div>
        <div className="bop-banca-fields__msg bop-text-muted">
          Calcolo offline da ABI + CAB + numero conto (CIN e check digit automatici).
        </div>
      </div>

      {/* Banca */}
      <div className="bop-form-group">
        <label className="bop-label">Banca</label>
        <div className="bop-banca-fields__iban-row">
          <input
            className="bop-input"
            type="text"
            value={value.banca}
            onChange={(e) => onChange({ banca: e.target.value })}
            placeholder="Nome della banca (auto-compilato dall'ABI)"
            disabled={disabled}
          />
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={openBancaPicker}
            disabled={disabled}
          >
            Cerca banca
          </button>
        </div>
      </div>

      {/* Filiale */}
      <div className="bop-form-group">
        <label className="bop-label">Filiale Banca</label>
        <div className="bop-banca-fields__iban-row">
          <input
            className="bop-input"
            type="text"
            value={value.filialeBanca}
            onChange={(e) => onChange({ filialeBanca: e.target.value })}
            placeholder="Città o nome filiale (auto-compilata dal CAB)"
            disabled={disabled}
          />
          <button
            type="button"
            className="bop-btn bop-btn-secondary"
            onClick={() => setShowSportelloModal(true)}
            disabled={disabled}
          >
            Cerca filiale
          </button>
        </div>
      </div>

      {/* Picker banca (registro nazionale) */}
      {showBancaModal && (
        <ModaleCercaRecord
          titolo="Cerca banca (ABI)"
          colonne={[
            { key: 'abi', label: 'ABI', mono: true },
            { key: 'denominazione', label: 'Denominazione' },
          ]}
          dati={bancheList}
          campoRicerca={['abi', 'denominazione']}
          onSeleziona={onSelectBanca}
          onChiudi={() => setShowBancaModal(false)}
        />
      )}

      {/* Picker filiale/sportello (ricerca server-side) */}
      {showSportelloModal && (
        <ModaleCercaSportello
          abi={/^\d{5}$/.test(value.abi) ? value.abi : undefined}
          onSeleziona={onSelectSportello}
          onChiudi={() => setShowSportelloModal(false)}
        />
      )}
    </div>
  );
}
